import PropTypes from 'prop-types';
import React, { memo } from 'react';
import classNames from 'classnames';
import { Map, List } from 'immutable';

import { ProjectDetails } from '../../../Project/ProjectDetails';
import { ResizableSection } from '../../../helpers/ResizableSection';
import Approvals from './MidPanel/Approvals';
import { Targets } from './Targets';
import { getIn, unwrap } from '../../../../utils/ChangeSpy';
import { BUSINESS_MODELS, INDUSTRY_CATEGORIES } from '../../../../utils/industryTagsHelper';

import style from './style.module.css';

/**
 * Add-on project component.
 *
 * @param props {Object}.
 * @param props.projectData {Immutable.Map} Project data.
 * @param props.users {Immutable.List} User list.
 * @param props.tags {Immutable.Map} Industry tags data.
 * @param props.approvals {Immutable.List} Approval list.
 * @param props.targets {Immutable.List} Target list.
 * @param props.common {Immutable.Map} Common object.
 * @param props.savedDetails {Object} Saved details shown in the widget while the edit popup is open.
 * @param props.onEdit {Function} Handle event of opening the project details popup.
 * @param props.onRowClick {Function} Handle event of clicking on a row.
 * @param props.onRowDoubleClick {Function} Handle event of double clicking on a row.
 * @param props.onRowInsert {Function} Handle event of adding a new row.
 * @param props.onRowChange {Function} Handle event of changing approval row.
 * @param props.onApprovalChange {Function} Handle event of changing approval cell.
 * @param props.onDeleteApprovalRow {Function} Handle event of deleting an approval list.
 * @param props.onSort {Function} Handle event of sorting table.
 * @param props.onSave {Function} Handle event of saving page.
 * @param {boolean} props.canEditData Whether user can edit any active element that can be changed or clicked.
 */
const ProjectComponent = ({
  canEditData,
  projectData,
  users,
  tags,
  approvals,
  targets,
  common,
  savedDetails,
  onEdit,
  onRowChange,
  onRowClick,
  onRowDoubleClick,
  onRowInsert,
  onDeleteApprovalRow,
  onSort,
  onSave,
  onApprovalChange,
}) => {
  const details = savedDetails || { projectData, users, tags };
  const industryNames = unwrap(details.tags.get(INDUSTRY_CATEGORIES))
    .concat(unwrap(details.tags.get(BUSINESS_MODELS)))
    .map(tag => tag.get('category'))
    .join(', ');
  const userNames = details.users
    .filter(user => getIn(user, 'id') > 0)
    .map(user => getIn(user, 'text'))
    .join(', ');

  return (
    <div className={classNames(style.host, 'project__containertab-pane active tab-target')} id="buy-slide-4">
      <div className={style.projectPanel}>
        <div className={classNames('row full-height', style.content)}>
          <form className="container-fluid full-height flexWrapper">
            <ProjectDetails
              industryNames={industryNames}
              isLoading={common.get('projectLoading')}
              onEdit={canEditData ? onEdit : undefined}
              projectData={details.projectData}
              projectLink={`/project/${details.projectData.get('id')}`}
              userNames={userNames}
              isBuyerHidden
            />
            <ResizableSection className={style.approvals} defaultHeight={208} storageKey="addOnApprovalsHeight">
              <Approvals
                approvals={approvals}
                canEditData={canEditData}
                common={common}
                onApprovalChange={onApprovalChange}
                onChange={onRowChange}
                onClick={onRowClick}
                onDelete={onDeleteApprovalRow}
                onRowDoubleClick={onRowDoubleClick}
                onRowInsert={onRowInsert}
                onSave={onSave}
                onSort={onSort}
              />
            </ResizableSection>
            <Targets
              canEditData={canEditData}
              common={common}
              onDoubleClick={onRowDoubleClick}
              onSort={onSort}
              targets={targets}
            />
          </form>
        </div>
      </div>
    </div>
  );
};

ProjectComponent.propTypes = {
  approvals: PropTypes.instanceOf(List).isRequired,
  canEditData: PropTypes.bool.isRequired,
  common: PropTypes.instanceOf(Map).isRequired,
  onEdit: PropTypes.func.isRequired,
  onRowChange: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
  onRowDoubleClick: PropTypes.func.isRequired,
  onRowInsert: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  projectData: PropTypes.instanceOf(Map).isRequired,
  savedDetails: PropTypes.shape({
    projectData: PropTypes.instanceOf(Map).isRequired,
    tags: PropTypes.instanceOf(Map).isRequired,
    users: PropTypes.instanceOf(List).isRequired,
  }),
  tags: PropTypes.instanceOf(Map).isRequired,
  targets: PropTypes.instanceOf(List).isRequired,
  users: PropTypes.instanceOf(List).isRequired,
};

/** Project component. */
export const Project = memo(ProjectComponent);
